import type { ShopifyProductNode } from "@/lib/shopify";
import type { Product } from "@/lib/fragrance-data";
import {
  deriveLiveNoteCards,
  noteCardsFromCharacterSlugs,
  type NoteCard,
} from "@/lib/note-cards";

export type ProductCopy = {
  name: string;
  subtitle: string;
  searchAnchor?: string;
  storyTagline: string;
  storyParagraphs: string[];
  storyClose: string;
  accents: string[];
  size: string;
  ingredients: string;
  allergens: string;
  attributes: string;
  sku: string;
  buyNote?: string;
  installment: string;
  noteSlugs: string[];
  noteCards: NoteCard[];
};

// ---------- HTML helpers ----------
function stripTags(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&#8217;|&rsquo;/g, "’")
    .replace(/&#8216;|&lsquo;/g, "‘")
    .replace(/&#8211;/g, "–")
    .replace(/&#8212;/g, "—")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function extractDetailsSection(html: string, summaryLabel: string): string | null {
  // Match <details ...>...<summary>...summaryLabel...</summary>BODY</details>
  const re = new RegExp(
    `<details[^>]*>\\s*<summary[^>]*>[\\s\\S]*?${summaryLabel}[\\s\\S]*?<\\/summary>([\\s\\S]*?)<\\/details>`,
    "i",
  );
  const m = html.match(re);
  return m ? m[1].trim() : null;
}

function extractParagraphs(html: string): string[] {
  const out: string[] = [];
  const re = /<p[^>]*>([\s\S]*?)<\/p>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const text = stripTags(m[1]);
    if (text) out.push(text);
  }
  return out;
}

// ---------- Story / size / ingredients parsing ----------
export function parseShopifyBody(descriptionHtml: string) {
  const storyHtml = extractDetailsSection(descriptionHtml, "Fragrance story");
  const sizeHtml = extractDetailsSection(descriptionHtml, "Size");
  const ingredientsHtml = extractDetailsSection(descriptionHtml, "Ingredients");

  const storyParagraphs = storyHtml ? extractParagraphs(storyHtml) : [];

  let size: string | null = null;
  if (sizeHtml) {
    const paras = extractParagraphs(sizeHtml);
    size = paras[0] ?? null;
  }

  let ingredients: string | null = null;
  let allergens: string | null = null;
  let attributes: string | null = null;
  if (ingredientsHtml) {
    const paras = extractParagraphs(ingredientsHtml);
    ingredients = paras[0] ?? null;
    // Allergen para usually mentions "allergens" or starts with *
    const allergenIdx = paras.findIndex((p) => /allergen|\*may contain/i.test(p));
    if (allergenIdx >= 0) allergens = paras[allergenIdx];
    const attrIdx = paras.findIndex((p) => /vegetarian|no phthalates|no parabens|paraben/i.test(p));
    if (attrIdx >= 0) attributes = paras[attrIdx];
  }

  return { storyParagraphs, size, ingredients, allergens, attributes };
}

// ---------- Accents metafield (Shopify list.single_line_text_field is a JSON array string) ----------
function parseAccents(value: string | null | undefined): string[] | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === "string");
  } catch {
    // not JSON — treat as bullet/comma separated
    return value
      .split(/\s*(?:•|·|\||,)\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return null;
}

// Extract a "Key accents: a • b • c" line from anywhere in the description HTML/text.
// Returns the parsed accents (if found) and the same text with the line removed.
const KEY_ACCENTS_RE = /key\s*accents?\s*[:\-–—]\s*([^.\n\r<]+?)(?=(?:\s*(?:[.\n\r]|▶|<|$)))/i;
export function extractKeyAccentsFromText(text: string): { accents: string[] | null; stripped: string } {
  if (!text) return { accents: null, stripped: text };
  const m = text.match(KEY_ACCENTS_RE);
  if (!m) return { accents: null, stripped: text };
  const list = m[1]
    .split(/\s*(?:•|·|\||,|;)\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (list.length === 0) return { accents: null, stripped: text };
  const stripped = text.replace(m[0], "").replace(/\s{2,}/g, " ").trim();
  return { accents: list, stripped };
}

// ---------- key_note_cards → NoteCard[] ----------
export function deriveLiveNoteSlugsFromCards(cards: NoteCard[]): string[] {
  return cards.map((c) => c.characterSlug ?? c.handle);
}

// ---------- Merge live data with local fallback ----------
export function buildProductCopy(local: Product, live: ShopifyProductNode | null): ProductCopy {
  if (!live) {
    return {
      name: local.name,
      subtitle: local.subtitle,
      searchAnchor: local.searchAnchor,
      storyTagline: local.storyTagline,
      storyParagraphs: local.storyParagraphs,
      storyClose: local.storyClose,
      accents: local.accents,
      size: local.size,
      ingredients: local.ingredients,
      allergens: local.allergens,
      attributes: local.attributes,
      sku: local.sku,
      buyNote: local.buyNote,
      installment: local.installment,
      noteSlugs: local.noteSlugs,
      noteCards: noteCardsFromCharacterSlugs(local.noteSlugs),
    };
  }

  const parsed = parseShopifyBody(live.descriptionHtml || "");
  const liveAccents = parseAccents(live.accents?.value);
  const liveNoteCards = deriveLiveNoteCards(live.keyNotes?.references?.nodes);
  const noteCards =
    liveNoteCards.length > 0 ? liveNoteCards : noteCardsFromCharacterSlugs(local.noteSlugs);
  const noteSlugs = deriveLiveNoteSlugsFromCards(noteCards);

  // Also scan the description (and parsed story paragraphs) for an inline
  // "Key accents: a • b • c" line — some products list them in the body
  // copy instead of the dedicated metafield.
  let inlineAccents: string[] | null = null;
  let cleanedStoryParagraphs = parsed.storyParagraphs.map((p) => {
    const { accents, stripped } = extractKeyAccentsFromText(p);
    if (accents && !inlineAccents) inlineAccents = accents;
    return stripped;
  }).filter((p) => p.length > 0);
  if (!inlineAccents) {
    const { accents } = extractKeyAccentsFromText(live.description || stripTags(live.descriptionHtml || ""));
    if (accents) inlineAccents = accents;
  }

  // Fallback: when the Shopify description has no <details> sections,
  // surface the plain description as story paragraphs so the page isn't empty.
  // CRITICAL: only render content BEFORE the first ▶ delimiter — anything after
  // is Size / Ingredients / etc. and must not leak into the Fragrance Story.
  const truncateAtMarker = (s: string) => s.split("▶")[0];
  const stripMarkers = (s: string) => s.replace(/▶/g, "").trim();
  let storyParagraphs = cleanedStoryParagraphs
    .map((p) => stripMarkers(truncateAtMarker(p)))
    .filter(Boolean);
  if (storyParagraphs.length === 0) {
    const rawHtml = truncateAtMarker(live.descriptionHtml || "");
    const htmlParas = extractParagraphs(rawHtml)
      .map((p) => stripMarkers(extractKeyAccentsFromText(p).stripped))
      .filter(Boolean);
    if (htmlParas.length > 0) {
      storyParagraphs = htmlParas;
    } else {
      const plain = truncateAtMarker((live.description || stripTags(rawHtml)).trim());
      if (plain) {
        const { stripped } = extractKeyAccentsFromText(plain);
        storyParagraphs = stripMarkers(stripped)
          .split(/\n\s*\n+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (storyParagraphs.length === 0 && stripped) storyParagraphs = [stripMarkers(stripped)];
      }
    }
    if (storyParagraphs.length === 0) storyParagraphs = local.storyParagraphs;
  }

  // Deduplicate against the tagline — some Shopify descriptions repeat
  // the opening sentence inside the Fragrance Story body copy.
  const taglineNorm = (live.storyTagline?.value || local.storyTagline || "").trim().toLowerCase().replace(/[\s]+/g, " ");
  if (taglineNorm) {
    storyParagraphs = storyParagraphs.filter((p) => {
      const pNorm = p.trim().toLowerCase().replace(/[\s]+/g, " ");
      return pNorm !== taglineNorm && !pNorm.startsWith(taglineNorm + " ");
    });
  }

  const resolvedAccents =
    local.accents && local.accents.length > 0
      ? local.accents
      : liveAccents && liveAccents.length > 0
        ? liveAccents
        : inlineAccents && inlineAccents.length > 0
          ? inlineAccents
          : [];

  return {
    name: live.title || local.name,
    subtitle: live.subtitle?.value || local.subtitle,
    searchAnchor: local.searchAnchor,
    storyTagline: live.storyTagline?.value || local.storyTagline,
    storyParagraphs,
    storyClose: live.storyClose?.value || local.storyClose,
    accents: resolvedAccents,
    // Always prefer the local size copy so legacy Shopify body text
    // (e.g. "order the Discovery Set ›") cannot leak into the page.
    size: local.size || parsed.size || "",
    ingredients: parsed.ingredients || local.ingredients,
    allergens: parsed.allergens || local.allergens,
    attributes: parsed.attributes || local.attributes,
    sku: live.skuDisplay?.value || local.sku,
    buyNote: live.buyNote?.value || local.buyNote,
    installment: live.installment?.value || local.installment,
    noteSlugs,
    noteCards,
  };
}
